
import { Component, computed, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminService } from '@shared-angular/services/admin.service';
import { ChatbotConfig, TemplateVar } from '@shared/index';
import { MarkdownModule } from 'ngx-markdown';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { StepperModule } from 'primeng/stepper';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BotIconComponent } from 'src/app/shared/components/bot-icon/bot-icon.component';
import { ProjectService } from 'src/app/shared/services/project.service';

type TemplateCard = {
  template: ChatbotConfig;
  shortDescription: string;
  searchText: string;
};

const SEARCH_THRESHOLD = 6;

@Component({
  selector: 'app-new-bot-modal',
  imports: [FormsModule, InputText, ButtonModule, DialogModule, IconFieldModule, InputIconModule, AutoFocusModule, MarkdownModule, StepperModule, TextareaModule, ToggleSwitchModule, BotIconComponent],
  templateUrl: './new-bot-modal.component.html',
  styleUrl: './new-bot-modal.component.scss'
})
export class NewBotModalComponent {
  public readonly visible = model(false);
  public readonly templates = input<ChatbotConfig[]>([]);

  protected name: string = '';
  protected templateId: string | null = null;
  protected readonly disabled = signal(false);
  protected readonly searchTerm = signal('');
  protected templateVarValues: TemplateVar[] = [];
  private readonly step = signal<'select' | 'config' | 'vars'>('select');

  // Superadmins erhalten vom Server auch Entwürfe; die gehören nicht in die Auswahl
  protected readonly availableTemplates = computed(() => this.templates().filter(t => !t.isTemplateDraft));

  protected readonly currentStep = computed(() =>
    this.step() === 'select' && this.availableTemplates().length === 0 ? 'config' : this.step()
  );

  protected readonly showSearch = computed(() => this.availableTemplates().length > SEARCH_THRESHOLD);

  private readonly templateCards = computed<TemplateCard[]>(() =>
    [...this.availableTemplates()]
      .sort((left, right) => left.name.localeCompare(right.name, 'de', { sensitivity: 'base' }))
      .map(template => ({
        template,
        shortDescription: this.getShortDescription(template.templateDescription ?? ''),
        searchText: `${template.name} ${template.templateDescription ?? ''}`.toLocaleLowerCase('de')
      }))
  );

  protected readonly filteredTemplateCards = computed(() => {
    const term = this.searchTerm().trim().toLocaleLowerCase('de');
    if (term === '') return this.templateCards();
    return this.templateCards().filter(card => card.searchText.includes(term));
  });

  public get selectedTemplate(): ChatbotConfig | undefined {
    if (!this.templateId) return undefined;
    return this.availableTemplates().find(t => t._id === this.templateId);
  }

  public get templateDescription() {
    return this.selectedTemplate?.templateDescription ?? '';
  }

  public get hasTemplateVars() {
    return (this.selectedTemplate?.templateVars.length ?? 0) > 0;
  }

  public get visibleTemplateVarValues(): TemplateVar[] {
    return this.templateVarValues.filter(tv => tv.showInWizard !== false);
  }

  public get submitLabel() {
    if (this.templateId && this.hasTemplateVars && this.currentStep() === 'config') {
      return $localize`:@@start-configuration:Konfiguration starten`;
    }
    return $localize`:@@create-new-bot:Neuen Bot erstellen`;
  }

  public get canCreateBot() {
    if (this.disabled()) return false;
    if (this.name.trim() === '') return false;
    return this.visibleTemplateVarValues.every(tv => this.isTemplateVarValid(tv));
  }

  constructor(
    private projectService: ProjectService,
    private adminService: AdminService
  ) { }

  public selectTemplate(template: ChatbotConfig | null) {
    this.templateId = template?._id ?? null;
    this.templateVarValues = [];
    this.step.set('config');
  }

  public back() {
    if (this.currentStep() === 'vars') {
      this.step.set('config');
      return;
    }
    this.templateId = null;
    this.templateVarValues = [];
    this.step.set('select');
  }

  public reset() {
    this.name = '';
    this.templateId = null;
    this.templateVarValues = [];
    this.searchTerm.set('');
    this.step.set('select');
  }

  public async submit() {
    if (this.templateId && this.hasTemplateVars && this.currentStep() === 'config') {
      this.goToTemplateVarStep();
      return;
    }

    await this.addBot();
  }

  public goToTemplateVarStep() {
    if (!this.projectService.selectedProject) return;
    if (!this.templateId || !this.selectedTemplate?.templateVars.length) return;
    this.name = this.name.trim();
    if (this.name === '') return;
    this.templateVarValues = this.selectedTemplate.templateVars.map((tv: TemplateVar) => ({ ...tv }));
    this.step.set('vars');
  }

  public isTemplateVarValid(tv: TemplateVar) {
    if (tv.type === 'string') {
      return tv.value.trim().length > 0;
    }
    if (tv.type === 'number') {

      return !Number.isNaN(tv.value);
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (tv.type === 'boolean') {
      return true; // value is always a boolean when type === 'boolean'
    }
    return false;
  }

  public async addBot() {
    if (!this.projectService.selectedProject) return;
    this.name = this.name.trim();
    if (this.name === "") return;
    let newBot: ChatbotConfig;
    this.disabled.set(true);
    if (!this.templateId) {
      if (this.projectService.selectedProject._id == "-") {
        newBot = await this.adminService.addBot(this.name);
      } else {
        newBot = await this.adminService.addBot(this.name, this.projectService.selectedProject._id);
      }
    } else {
      const extraAttr: Partial<ChatbotConfig> = { name: this.name };
      if (this.projectService.selectedProject._id != "-") {
        extraAttr.projectId = this.projectService.selectedProject._id;
      }
      if (this.templateVarValues.length > 0) {
        extraAttr.templateVars = this.templateVarValues;
      }
      newBot = await this.adminService.copyBot(this.templateId, extraAttr);
    }
    this.projectService.bots = [...this.projectService.bots, newBot];
    this.disabled.set(false);
    this.visible.set(false);
  }

  // Erster Absatz ohne Überschriften, Markdown-Syntax entfernt
  private getShortDescription(markdown: string): string {
    const withoutHeadings = markdown
      .split('\n')
      .filter(line => !/^\s*#{1,6}\s/.test(line))
      .join('\n');
    const paragraph = withoutHeadings
      .split(/\n\s*\n/)
      .map(p => p.trim())
      .find(p => p !== '') ?? '';
    return paragraph
      .replace(/<[^>]+>/g, '')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '')
      .replace(/^\s*>\s?/gm, '')
      .replace(/[*`~]+/g, '')
      .replace(/(?<!\w)_+|_+(?!\w)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

}
